import React, { Component, Fragment, ReactNode } from 'react';
import FormicaLabel from '../../../custom/FormicaLabel';
import { RootState } from '../../../../redux/store';
import { VelocityVariableState } from '../../../../redux/reducers/VelocityVariableReducer';
import { enterComponent, leaveComponent } from '../../../../redux/actions/ComponentActions';
import { changeVelocityVariable, changeVelocityVariableIsUpdate, clearVelocityVariable } from '../../../../redux/actions/VelocityVariableActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import {
  ArchiosExpressionProfile,
  ExpressionBuilderView,
  ExpressionContextMenuComponent,
  ExpressionTokenComponent,
  StandardExpressionBuilderView,
  Syntax,
  TokenVariable,
} from '@trlogic/ui-pack';
import VariableType from '../../../../constant/VariableType';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../../constant/CollectionNames';
import { StreamJsonModel } from '../../../../domain/stream/stream/StreamDomain';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import DataType from '../../../../constant/DataType';
import ExpressionBridge from '../../../../util/ExpressionBridge';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import Computation = subscribe.Computation;
import SubscriptionContext = subscribe.SubscriptionContext;
import NavigationUtil from '../../../../util/NavigationUtil';
import LanguageUtil from '../../../../util/LanguageUtil';
import { ExpressionUtil } from '../../../../util/ExpressionUtil';

class VelocityVariableSelectComponent extends Component<Props, State> {
  public state = new State();

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);
  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);

  private computation: Computation | undefined;

  public render(): ReactNode {
    const {
      velocityVariable: { velocityVariable },
    } = this.props;
    const { expressionView } = this.state;
    if (expressionView == undefined) {
      return null;
    }
    return (
      <div className={'flex w-full flex-col gap-y-1.5 overflow-x-hidden max-w-full shrink-0'}>
        <FormicaLabel label={LanguageUtil.get({ id: 'common.label.select', defaultMessage: 'Select' })} />
        <div className={'flex flex-col w-full border-[1px] h-full overflow-scroll border-[#E0E4E8] rounded-[4px] px-5 py-5 bg-main-gray'}>
          <div className='' id='expression_builder_select'>
            {expressionView && (
              <Fragment>
                <ExpressionTokenComponent syntax={expressionView.getModel()} view={expressionView} />
              </Fragment>
            )}
            <ExpressionContextMenuComponent context={expressionView.getContext()} close={this.onClose} />
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const transactionSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS);
    const streamSubscription = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.STREAMS);
    this.computation = subscribe.Tracker.autorun(this.tracker, [...transactionSubscriptions, ...streamSubscription]);
    this.build();
  }

  public componentDidUpdate(prevProps: Readonly<Props>): void {
    const nextReturnType = this.getReturnTypeForFunction(this.props.velocityVariable.velocityVariable.function);
    if (this.state.expressionView?.getReturnType().dataType != nextReturnType.dataType) {
      this.state.expressionView?.setReturnType(nextReturnType);
    }
    if (this.props.update != prevProps.update) {
      this.refreshVariables();
    }
    if (this.props.disabled != prevProps.disabled) {
      if (this.props.disabled) {
        this.state.expressionView?.pause();
      } else {
        this.state.expressionView?.resume();
      }
    }
    if (this.props.velocityUpdated != prevProps.velocityUpdated) {
      this.state.expressionView?.terminate();
      this.setState({ expressionView: undefined }, this.build);
    }
  }

  private tracker = async () => {
    const { expressionView } = this.state;
    if (
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.STREAMS).some((e) => e.ready()) ||
      this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS).some((e) => e.ready())
    ) {
      const variables = await this.prepareVariables();
      expressionView?.getContext().setVariables(variables);
    }
  };

  private build = async () => {
    const expression = this.props.velocityVariable.velocityVariable.select;
    const variables: Record<string, TokenVariable[]> = await this.prepareVariables();
    await this.expressionBridge.start(expression, variables);
    const expressionView: ExpressionBuilderView = new StandardExpressionBuilderView({
      el: '#expression_builder_select',
      model: expression,
      profile: new ArchiosExpressionProfile(),
      returnType: this.getReturnTypeForFunction(this.props.velocityVariable.velocityVariable.function),
      onCtrlToken: ExpressionUtil.onNavigateToken,
    });
    expressionView.getContext().setVariables(variables);
    expressionView.onChange(this.onChangeSyntax as any);
    this.setState({ expressionView }, () => {
      expressionView.initialize();
      if (this.props.disabled) expressionView.pause();
    });
  };

  private getReturnTypeForFunction = (func: string): { dataType: DataType } => {
    switch (func) {
      case 'distinct':
        return { dataType: DataType.STRING };
      case 'count':
      case 'mode':
      case 'anti_mode':
        return { dataType: DataType.UNKNOWN };
      default:
        return { dataType: DataType.NUMBER };
    }
  };

  private prepareVariables = async (): Promise<Record<string, TokenVariable[]>> => {
    const {
      velocityVariable: { velocityVariable, permittedTenantStores },
    } = this.props;
    if (velocityVariable.stream == undefined) return {};
    let stream: StreamJsonModel | undefined = await this.repository.find(
      QueryBuilder.select().from(CollectionNames.STREAMS).where(eq('name', velocityVariable.stream)).build(),
    );
    if (velocityVariable.tenant && velocityVariable.tenant != NavigationUtil.getTenant()) {
      stream = permittedTenantStores[velocityVariable.tenant]?.find((s) => s.name == velocityVariable.stream);
    }
    if (stream == undefined) return {};
    const event: TransactionJsonModel | undefined = await this.repository.find(
      QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('name', velocityVariable.transaction)).build(),
    );
    if (event == undefined) return {};
    const streamVariables = stream.columns.map((c) => ({
      type: VariableType.STREAM_ROW_VARIABLE as any,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.STREAM_ROW_VARIABLE as any,
        properties: c.properties,
        name: c.name,
        description: '',
        args: {
          stream: stream!.name,
        },
      },
    }));
    const eventVariables = event.variables.map((v) => ({
      type: VariableType.EVENT_VARIABLE as any,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.EVENT_VARIABLE as any,
        properties: v.properties,
        name: v.name,
        description: v.description,
        args: {
          event: event.name,
        },
      },
    }));
    return {
      [VariableType.EVENT_VARIABLE]: eventVariables,
      [VariableType.STREAM_ROW_VARIABLE]: streamVariables,
    };
  };

  public componentWillUnmount() {
    const { expressionView } = this.state;
    expressionView?.terminate();
    this.computation?.stop();
  }

  private onChangeSyntax = (updatedExpression: Syntax): void => {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    velocityVariable.select = updatedExpression;
    changeVelocityVariable(velocityVariable);
  };

  private onClose() {}

  private refreshVariables = async () => {
    const { expressionView } = this.state;
    if (expressionView == undefined) return;
    const expression = this.props.velocityVariable.velocityVariable.select;
    const variables: Record<string, TokenVariable[]> = await this.prepareVariables();
    await this.expressionBridge.start(expression, variables);
    expressionView.getContext().setVariables(variables);
  };
}

interface Props extends ReduxProps {
  update: number;
  disabled: boolean;
  velocityUpdated: number;
}

class State {
  expressionView: ExpressionBuilderView | undefined;
  variables: Record<string, TokenVariable[]> = {};
}

const mapStateToProps = (state: RootState) => ({
  velocityVariable: state.velocityVariable as VelocityVariableState,
});

const mapDispatchToProps = { enterComponent, leaveComponent, changeVelocityVariableIsUpdate, clearVelocityVariable, changeVelocityVariable };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(VelocityVariableSelectComponent);
